import { CheckCircle2, Loader2, RotateCw, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';

/** Lifecycle of a single file download, as reflected in the toast. */
export type DownloadToastStatus = 'downloading' | 'done' | 'failed';

interface Props {
  /** Human context — what is being downloaded (e.g. "Results Readout Deck"). */
  label: string;
  status: DownloadToastStatus;
  /** Resolved error message, shown only on `failed`. */
  errorMessage?: string | undefined;
  /** Re-runs the download; only rendered on `failed`. */
  onRetry?: (() => void) | undefined;
}

/** Status glyph: spinner while fetching, check on success, cross on failure. */
function StatusIcon({ status }: { status: DownloadToastStatus }) {
  if (status === 'done') {
    return <CheckCircle2 aria-hidden="true" className="text-success h-4 w-4 shrink-0" />;
  }
  if (status === 'failed') {
    return <XCircle aria-hidden="true" className="text-error h-4 w-4 shrink-0" />;
  }
  return <Loader2 aria-hidden="true" className="text-primary h-4 w-4 shrink-0 animate-spin" />;
}

/**
 * Toast body for a one-shot authenticated file download. Unlike the export /
 * analytics-sync toasts there is no progress bar: a blob `GET` reports no
 * intermediate progress, so a percentage here would be fabricated.
 */
export function DownloadToast({ label, status, errorMessage, onRetry }: Props) {
  const { t } = useTranslation('common');

  const heading =
    status === 'done'
      ? t('downloadToast.title.done', 'Download started')
      : status === 'failed'
        ? t('downloadToast.title.failed', 'Download failed')
        : t('downloadToast.title.downloading', 'Preparing your download');

  return (
    <div className="flex w-full flex-col gap-2">
      <span className="text-foreground flex items-center gap-1.5 text-sm font-semibold">
        <StatusIcon status={status} />
        {heading}
      </span>

      <p className="text-muted-foreground truncate text-xs">{label}</p>

      {/* The browser exposes no "save complete" event and we can't open the
          downloads folder, so on success we just point the user at it. */}
      {status === 'done' && (
        <p className="text-muted-foreground text-xs">
          {t('downloadToast.doneHint', 'Check your browser downloads for the file.')}
        </p>
      )}

      {status === 'failed' && errorMessage && <p className="text-error text-xs">{errorMessage}</p>}

      {status === 'failed' && onRetry && (
        <div className="mt-1 flex items-center justify-end">
          <Button type="button" size="sm" onClick={onRetry} className="cursor-pointer">
            <RotateCw aria-hidden="true" className="mr-1.5 h-3.5 w-3.5" />
            {t('downloadToast.actions.retry', 'Retry')}
          </Button>
        </div>
      )}
    </div>
  );
}
